import { IndexContainer } from '../../../components-common/containers/IndexContainer';
import {
  editParameterScreen,
  newParameterScreen,
  parametersTab,
  toolbar_components,
} from './_common_props';
import { layoutWorkspace } from '../../../_website/_common/layout.workspace';
import { TabbedPage } from '../../../components-common/tabs/TabbedPage';
import { PageFormToolbar } from '../../../components-common/layout/views/PageFormToolbar';
import { TypeWindowScreenTabs } from '../../../components-domain/bim-domain/elements/window/TypeWindowScreenTabs.';
import { clone } from 'ramda';
import { getWindowTypeStructureSchema } from '../../../components-domain/bim-domain/elements/window/schema';
import { compDBAdditionalCheck } from '../../../components-domain/bim-domain/defaults';

/**
 * Configuration Object: typesFormScreen
 *
 * This object defines the configuration for the types form screen specific to window types.
 * It specifies the component, form, template, validation schema, and initial empty values.
 *
 * @property {React.Component} Component - The component to render the form toolbar.
 * @property {React.Component} form - The form component used for managing window type screen tabs.
 * @property {string} template - The template URL for the parameters.
 * @property {Object} schema - The validation schema for the form.
 * @property {Object} empty - The initial empty values for the form fields.
 */
const typesFormScreen = {
  Component: PageFormToolbar,
  form: TypeWindowScreenTabs,
  template: '/buildingms/categories/parameters',
  schema: getWindowTypeStructureSchema(),
  empty: {
    id: '',
    name: '',
    style: 'WALL_TO_WALL',
    height: { length: 1, position: 'ABSOLUTE' },
    sillHeight: { length: .5, position: 'ABSOLUTE' },
    width: { length: 1, position: 'ABSOLUTE' },
    description: '',
    layers: [],
    category: 'WINDOWS',
    typeParameters: [],
  },
};

/**
 * Window Tabs Configuration
 *
 * Defines the configuration for the tabs within the windows section.
 *
 * @type {Array}
 * @property {Object} id - The unique identifier for the tab.
 * @property {string} title - The title of the tab.
 * @property {Array} components - The array of components to be rendered within the tab.
 */
const windowTabs = [
  {
    id: 'window_types',
    title: 'Types',
    components: [
      {
        Component: IndexContainer,
        props: {
          toolbar: {
            ...toolbar_components,
            left: [
              {
                id: 'create',
                kind: 'create',
                onAction: (props: any) => props.navigate('types/new'),
              } as any,
            ],
          },
          table: {
            cat: 'table',
            type: 'editor',
            id: 'table_components',
            props: {
              source: {
                type: 'REMOTE',
                url: '/buildingsms/types/elements',
                key: '/buildingsms/types/elements',
                urlParams: { category: 'WINDOWS' },
              },
              columns: [
                'name',
                'description',
                { id: 'sourceType' },
                'createdDate',
                'modifiedDate',
              ],
              actions: [
                {
                  id: 'edit',
                  onClick: (props: any) =>
                    props.navigate(`types/${props.row.id}`),
                  additionalChecks: compDBAdditionalCheck,
                  displayTooltip: true,
                  tooltipText: 'Edit type',
                },
                {
                  id: 'copy',
                  actionToDispatch: {
                    payload: {
                      copy: '/buildingsms/types/elements/cloneElement',
                    },
                  },
                  displayTooltip: true,
                  tooltipText: 'Duplicate type',
                } as any,
                {
                  id: 'delete',
                  actionToDispatch: {
                    payload: {
                      endpointToDelete: '/buildingsms/types/elements',
                    },
                  },
                  additionalChecks: compDBAdditionalCheck,
                  displayTooltip: true,
                  tooltipText: 'Delete type',
                },
              ],
            },
          },
        },
      },
    ],
  },
  parametersTab('WINDOWS'),
];

/**
 * Windows Section Configuration
 *
 * Defines the configuration for the windows section, including paths, layouts, and screens.
 *
 * @type {Object}
 * @property {string} id - The unique identifier for the section.
 * @property {string} path - The base path for the section.
 * @property {string} tmp - The template type for the section.
 * @property {Object} layout - The layout configuration for the section.
 * @property {React.FC} Component - The component to be rendered for the section.
 * @property {Array} screens - The array of screen configurations within the section.
 */
export const windowsSection: any = {
  id: 'windows',
  title: 'Windows',
  path: 'windows',
  tmp: 'tabbed',
  layout: layoutWorkspace,
  Component: TabbedPage,
  screens: [
    {
      id: 'windows',
      path: '',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tabs: windowTabs,
    },
    {
      id: 'windows_types_create',
      title: 'Create New Window Type',
      path: 'types/new',
      tmp: 'create',
      left: ['create'],
      right: ['back'],
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      ...clone(typesFormScreen),
    },
    {
      id: 'windows_types_edit',
      title: 'Edit Window Type',
      path: 'types/:windowTypeId',
      tmp: 'edit',
      left: ['save'],
      right: ['back'],
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      editSource: '/buildingsms/types/elements/windows/',
      idPropSource: 'windowTypeId',
      ...clone(typesFormScreen),
    },
    newParameterScreen('WINDOWS'),
    editParameterScreen('WINDOWS'),
  ],
};
